// ClaimantTab — ATTICUS-CLAIMANT-INJURY-DASHBOARD-1 (P2). First-class native
// Claimant surface: 6 sections (General / Employment / Loss & Injury / Medical /
// Legal / Communication), Atticus-native cards + compact field grids + inline
// edit/save (NOT a recreation of the VCA form wall). Native fields first; until a
// claimant_details row is saved, the read view falls back to claim.insured_* + VCA
// JSON (provenance-tagged). Canonical loss fields (date_of_loss / loss_location /
// loss_state / loss_description) edit the claim itself. SSN shown last-4 masked.
import { useState, useEffect, useMemo } from "react";
import { apiFetch, fmt, canOnClaim, getUser } from "../api";
import { StatusPill, EmptyState, Spinner, KeyValue } from "./ui";

const WAGE_BASIS = [["", "—"], ["hourly", "Hourly"], ["daily", "Daily"], ["weekly", "Weekly"], ["monthly", "Monthly"], ["unknown", "Unknown"]];
const WORK_STATUS = [["", "—"], ["unknown", "Unknown"], ["working", "Working"], ["off_work", "Off Work"], ["light_duty", "Light Duty"], ["returned_to_work", "Returned to Work"], ["terminated", "Terminated"]];
const LITIGATION = [["", "—"], ["not_represented", "Not Represented"], ["represented_pre_suit", "Represented (pre-suit)"], ["suit_filed", "Suit Filed"], ["in_litigation", "In Litigation"], ["settled", "Settled"], ["closed", "Closed"], ["unknown", "Unknown"]];
const COMM_METHOD = [["", "—"], ["phone", "Phone"], ["email", "Email"], ["mail", "Mail"], ["through_counsel", "Through Counsel"], ["do_not_contact", "Do Not Contact"]];
const CLAIMANT_TYPE = [["individual", "Individual"], ["company", "Company / Entity"]];
const labelOf = (opts, v) => (opts.find(o => o[0] === (v ?? "")) || [, v])[1] || "—";

const SECTIONS = [
  { key: "general", label: "Overview", fields: [
    { k: "claimant_type", l: "Claimant Type", t: "sel", opts: CLAIMANT_TYPE, pill: true },
    { k: "display_name", l: "Display Name" },
    { k: "first_name", l: "First Name" }, { k: "middle_name", l: "Middle Name" }, { k: "last_name", l: "Last Name" },
    { k: "company_name", l: "Company Name", when: f => f.claimant_type === "company" },
    { k: "date_of_birth", l: "Date of Birth", t: "date" },
    { k: "gender", l: "Gender" }, { k: "marital_status", l: "Marital Status" }, { k: "num_dependents", l: "Dependents", t: "num" },
    { k: "driver_license_number", l: "Driver License #" },
    { k: "ssn_last4", l: "SSN (last 4)", t: "ssn" },
    { k: "primary_phone", l: "Primary Phone" }, { k: "cell_phone", l: "Cell Phone" }, { k: "email", l: "Email" },
    { k: "address_line1", l: "Address 1" }, { k: "address_line2", l: "Address 2" },
    { k: "city", l: "City" }, { k: "state_province", l: "State / Province" }, { k: "postal_code", l: "Postal / Zip" }, { k: "country", l: "Country" },
    { k: "alt_contact_name", l: "Alternate Contact" }, { k: "alt_contact_phone", l: "Alternate Phone" },
    { k: "general_notes", l: "General Notes", t: "area", full: true },
  ]},
  { key: "employment", label: "Employment", fields: [
    { k: "employer_name", l: "Employer" }, { k: "employer_address", l: "Employer Address" }, { k: "employer_phone", l: "Employer Phone" },
    { k: "employee_number", l: "Employee #" }, { k: "job_title", l: "Job Title / Position" },
    { k: "duties", l: "Duties", t: "area", full: true }, { k: "time_employed", l: "Time Employed" },
    { k: "wage_amount", l: "Salary / Wage", t: "money" }, { k: "wage_basis", l: "Wage Basis", t: "sel", opts: WAGE_BASIS },
    { k: "maintenance_rate", l: "Maintenance Rate", t: "money" }, { k: "max_benefit_cap", l: "Max Benefit Cap", t: "money" },
    { k: "crew_status", l: "Crew Status" }, { k: "is_seaman_jones_act", l: "Seaman / Jones Act", t: "bool" },
    { k: "claim_type_irb_cg", l: "Claim Type (IRB/CG)" },
    { k: "work_status", l: "Work Status", t: "sel", opts: WORK_STATUS, pill: true }, { k: "return_to_work_date", l: "Return to Work Date", t: "date" },
    { k: "employment_notes", l: "Employment Notes", t: "area", full: true },
  ]},
  // CLAIM-WS-IA B1 (2026-06-24): Loss moved to the CLAIM (Claim tab → Loss Information).
  // The Claimant tab now owns INJURY only. (Loss columns live on `claims` after mig 104.)
  { key: "injury", label: "Injury", fields: [
    { k: "injury_illness", l: "Injury / Illness" }, { k: "nature_of_injury", l: "Nature of Injury" }, { k: "source_of_injury", l: "Source of Injury" },
    { k: "severity_of_injury", l: "Severity of Injury" },
    { k: "body_parts", l: "Body Parts Affected", t: "bodyparts", full: true },
    { k: "injury_description", l: "Injury Description", t: "area", full: true },
  ]},
  { key: "medical", label: "Medical", fields: [
    { k: "family_doctor_name", l: "Family Doctor" }, { k: "family_doctor_phone", l: "Family Doctor Phone" }, { k: "family_doctor_fax", l: "Family Doctor Fax" },
    { k: "family_doctor_address", l: "Family Doctor Address" }, { k: "family_doctor_city", l: "City" }, { k: "family_doctor_state", l: "State" }, { k: "family_doctor_postal", l: "Postal" }, { k: "family_doctor_country", l: "Country" },
    { k: "taken_to_hospital", l: "Taken to Hospital", t: "bool" }, { k: "hospital_name", l: "Hospital Name" },
    { k: "post_loss_doctor", l: "Post-Loss Doctor" }, { k: "post_loss_doctor_phone", l: "Post-Loss Doctor Phone" },
    { k: "rehab_clinic1_name", l: "Rehab Clinic 1" }, { k: "rehab_clinic1_address", l: "Rehab 1 Address" }, { k: "rehab_clinic1_phone", l: "Rehab 1 Phone" },
    { k: "rehab_clinic2_name", l: "Rehab Clinic 2" }, { k: "rehab_clinic2_address", l: "Rehab 2 Address" }, { k: "rehab_clinic2_phone", l: "Rehab 2 Phone" },
    { k: "prior_injuries", l: "Prior Injuries / Conditions", t: "area", full: true }, { k: "post_loss_injuries", l: "Post-Loss Injuries", t: "area", full: true },
    { k: "treatment_status", l: "Current Treatment Status" }, { k: "work_restrictions", l: "Work Restrictions", t: "area", full: true },
    { k: "mmi_status", l: "MMI Status" }, { k: "mmi_date", l: "MMI Date", t: "date" },
    { k: "surgery_flag", l: "Surgery", t: "bool" }, { k: "surgery_date", l: "Surgery Date", t: "date" },
    { k: "last_medical_update", l: "Last Medical Update", t: "date" }, { k: "next_medical_followup", l: "Next Medical Follow-Up", t: "date" },
    { k: "medical_notes", l: "Medical Notes", t: "area", full: true },
  ]},
  { key: "legal", label: "Legal / Representation", fields: [
    { k: "is_represented", l: "Represented?", t: "bool" },
    { k: "law_firm_name", l: "Law Firm" }, { k: "law_firm_phone", l: "Law Firm Phone" }, { k: "law_firm_fax", l: "Law Firm Fax" },
    { k: "law_firm_address", l: "Law Firm Address" }, { k: "law_firm_city", l: "City" }, { k: "law_firm_state", l: "State" }, { k: "law_firm_postal", l: "Postal" }, { k: "law_firm_country", l: "Country" },
    { k: "attorney_name", l: "Attorney / Legal Rep" }, { k: "attorney_direct_line", l: "Direct Line" }, { k: "attorney_email", l: "Attorney Email" },
    { k: "case_number", l: "Case Number" },
    { k: "date_suit_received", l: "Date Suit Received", t: "date" }, { k: "apparent_service_date", l: "Apparent Service Date", t: "date" },
    { k: "date_suit_assigned", l: "Date Suit Assigned", t: "date" }, { k: "stipulation_date", l: "Stipulation Date", t: "date" },
    { k: "litigation_status", l: "Litigation Status", t: "sel", opts: LITIGATION, pill: true },
    { k: "defense_counsel", l: "Defense Counsel" }, { k: "defense_counsel_contact", l: "Defense Counsel Contact" },
    { k: "legal_notes", l: "Legal Notes", t: "area", full: true },
  ]},
  { key: "comm", label: "Communication", fields: [
    { k: "preferred_comm_method", l: "Preferred Method", t: "sel", opts: COMM_METHOD, pill: true },
    { k: "do_not_contact", l: "Do Not Contact Claimant", t: "bool" },
    { k: "communication_restrictions", l: "Communication Restrictions", t: "area", full: true },
    { k: "claimant_delivery_instructions", l: "Claimant Delivery Instructions", t: "area", full: true },
    { k: "attorney_delivery_instructions", l: "Attorney Delivery Instructions", t: "area", full: true },
    { k: "sensitive_handling_notes", l: "Sensitive Handling Notes", t: "area", full: true },
  ]},
];

function ReadValue({ f, form }) {
  const v = form[f.k];
  if (f.t === "bodyparts") return (Array.isArray(v) && v.length)
    ? <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>{v.map(b => <span key={b} className="badge" style={{ background: "#eef2ff", color: "#3730a3", fontSize: 10 }}>{b}</span>)}</div>
    : <span style={{ color: "var(--text3)" }}>—</span>;
  if (v == null || v === "") return <span style={{ color: "var(--text3)" }}>—</span>;
  if (f.t === "ssn") return <span className="mono">•••• {String(v).slice(-4)}</span>;
  if (f.t === "bool") return <StatusPill sm status={v ? "yes" : "no"}>{v ? "Yes" : "No"}</StatusPill>;
  if (f.pill) return <StatusPill sm status={String(v)}>{labelOf(f.opts, v)}</StatusPill>;
  if (f.t === "sel") return <span>{labelOf(f.opts, v)}</span>;
  if (f.t === "date") return <span>{fmt.date(v)}</span>;
  if (f.t === "money") return <span className="mono">{fmt.money(v)}</span>;
  return <span style={{ whiteSpace: f.t === "area" ? "pre-wrap" : "normal" }}>{String(v)}</span>;
}

function EditValue({ f, form, set, bodyParts }) {
  const v = form[f.k] ?? (f.t === "bodyparts" ? [] : "");
  const inp = { width: "100%", padding: "5px 8px", border: "1px solid var(--border)", borderRadius: 5, fontSize: 13, boxSizing: "border-box" };
  if (f.ro) return <ReadValue f={f} form={form} />;
  if (f.t === "bodyparts") {
    const sel = new Set(v || []);
    return (
      <div style={{ maxHeight: 150, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 5, padding: 6, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px,1fr))", gap: "2px 10px" }}>
        {bodyParts.map(b => (
          <label key={b} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, cursor: "pointer" }}>
            <input type="checkbox" checked={sel.has(b)} onChange={e => { const n = new Set(sel); e.target.checked ? n.add(b) : n.delete(b); set(f.k, Array.from(n)); }} />{b}
          </label>
        ))}
      </div>
    );
  }
  if (f.t === "bool") return <input type="checkbox" checked={!!v} onChange={e => set(f.k, e.target.checked)} />;
  if (f.t === "sel") return <select style={inp} value={v} onChange={e => set(f.k, e.target.value)}>{f.opts.map(o => <option key={o[0]} value={o[0]}>{o[1]}</option>)}</select>;
  if (f.t === "area") return <textarea style={{ ...inp, minHeight: 52, resize: "vertical" }} value={v} onChange={e => set(f.k, e.target.value)} />;
  if (f.t === "date") return <input type="date" style={inp} value={v ? String(v).slice(0, 10) : ""} onChange={e => set(f.k, e.target.value || null)} />;
  if (f.t === "num") return <input type="number" style={inp} value={v} onChange={e => set(f.k, e.target.value === "" ? null : Number(e.target.value))} />;
  if (f.t === "money") return <input type="number" step="0.01" style={inp} value={v} onChange={e => set(f.k, e.target.value === "" ? null : Number(e.target.value))} />;
  if (f.t === "ssn") return <input maxLength={4} placeholder="1234" style={inp} value={v} onChange={e => set(f.k, e.target.value.replace(/\D/g, "").slice(-4))} />;
  return <input style={inp} value={v} onChange={e => set(f.k, e.target.value)} />;
}

// LD-DESIGN-012 pattern 2 (entity-profile anatomy): the Overview sub-tab read
// view is three cards — Profile + Readiness + Quick Facts. Edit mode and every
// other sub-tab keep the full field grid.
function ClaimantOverviewCards({ form, emptyCount, canEdit }) {
  const name = form.display_name || (form.first_name || form.last_name ? `${form.first_name || ""} ${form.last_name || ""}`.trim() : "") || form.company_name || "";
  const address = [form.address_line1, form.address_line2, form.city].filter(Boolean).join(", ");
  // Honesty rule: every check below derives from fields the /claimant payload
  // actually carries (claimant_details columns already rendered by SECTIONS).
  const checks = [
    { key: "name", label: "Name recorded", ok: !!name },
    { key: "dob", label: "Date of birth", ok: !!form.date_of_birth },
    { key: "employment", label: "Employment captured", ok: !!(form.employer_name || form.job_title) },
    { key: "injury", label: "Injury captured", ok: !!(form.injury_illness || form.nature_of_injury || form.injury_description) },
    { key: "rep", label: "Representation known", ok: form.is_represented != null || !!form.litigation_status },
    { key: "medical", label: "Medical provider recorded", ok: !!(form.family_doctor_name || form.post_loss_doctor || form.hospital_name) },
  ];
  const done = checks.filter(c => c.ok).length;
  const card = { flex: "1 1 260px", minWidth: 240, alignSelf: "flex-start" };
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "stretch" }}>
      <div className="card" style={card}>
        <div className="card-header"><h3>Claimant Profile</h3></div>
        <div className="card-body">
          <KeyValue label="Display Name">{name || null}</KeyValue>
          <KeyValue label="Date of Birth">{form.date_of_birth ? fmt.date(form.date_of_birth) : null}</KeyValue>
          <KeyValue label="Phone">{form.primary_phone || form.cell_phone || null}</KeyValue>
          <KeyValue label="Email">{form.email || null}</KeyValue>
          <KeyValue label="Address">{address || null}</KeyValue>
          <KeyValue label="State">{form.state_province || null}</KeyValue>
          {emptyCount > 0 && (
            <div style={{ marginTop: 10, fontSize: 11.5, color: "var(--text3)" }}>
              {emptyCount} more field{emptyCount > 1 ? "s" : ""} not yet filled{canEdit ? " · click Edit Claimant to add" : ""}.
            </div>
          )}
        </div>
      </div>
      <div className="card" style={card}>
        <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3>Claimant Readiness</h3>
          <span style={{ fontSize: 11, color: "var(--text2)" }}>{done}/{checks.length} complete</span>
        </div>
        <div className="card-body">
          <div style={{ height: 4, borderRadius: 2, background: "#e2e8f0", marginBottom: 10, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${checks.length ? Math.round((done / checks.length) * 100) : 0}%`,
                          background: done === checks.length ? "#16a34a" : "#d97706", transition: "width 0.2s" }} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {checks.map(ch => (
              <div key={ch.key} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12.5 }}>
                <span style={{ color: ch.ok ? "#16a34a" : "#d97706", fontWeight: 700, flexShrink: 0, width: 14 }}>{ch.ok ? "✓" : "⚠"}</span>
                <span style={{ color: "var(--text)", fontWeight: ch.ok ? 400 : 600 }}>{ch.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="card" style={card}>
        <div className="card-header"><h3>Quick Facts</h3></div>
        <div className="card-body">
          {/* Only fields the claimant payload actually returns (no hire date /
              years of experience / primary language columns exist yet). */}
          <KeyValue label="Employment Status">{form.work_status ? <StatusPill sm status={String(form.work_status)}>{labelOf(WORK_STATUS, form.work_status)}</StatusPill> : null}</KeyValue>
          <KeyValue label="Occupation">{form.job_title || null}</KeyValue>
          <KeyValue label="Time Employed">{form.time_employed || null}</KeyValue>
          <KeyValue label="Employer">{form.employer_name || null}</KeyValue>
        </div>
      </div>
    </div>
  );
}

export default function ClaimantTab({ claimId, claim, onClaimUpdated, onWorkChanged }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [bodyParts, setBodyParts] = useState([]);
  const [active, setActive] = useState("general");
  const canEdit = useMemo(() => canOnClaim("edit_claim", claim, getUser()) || canOnClaim("edit_claim_basic", claim, getUser()), [claim]);

  const seed = (d) => {
    const base = d.materialized ? d.claimant : (d.fallback || {});
    setForm({ ...base, ...(d.claim_canonical || {}), vessel_name: claim?.vessel_name || (d.fallback || {}).vessel_name || null });
  };
  const load = () => apiFetch(`/claims/${claimId}/claimant`).then(d => { setData(d); seed(d); }).catch(e => setErr(String(e.message || e)));
  useEffect(() => { load(); apiFetch(`/claimant/body-parts`).then(setBodyParts).catch(() => {}); /* eslint-disable-next-line */ }, [claimId]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true); setErr("");
    try {
      const d = await apiFetch(`/claims/${claimId}/claimant`, { method: "PUT", body: form });
      setData(d); seed(d); setEditing(false);
      if (onClaimUpdated && claim) onClaimUpdated({ ...claim, ...(d.claim_canonical || {}) });
      onWorkChanged && onWorkChanged();   // Overview claimant tile + readiness surfaces re-sync
    } catch (e) { setErr(String(e.message || e)); } finally { setBusy(false); }
  };
  const cancel = () => { if (data) seed(data); setEditing(false); setErr(""); };

  if (!data) return <Spinner label="Loading claimant…" />;
  const sec = SECTIONS.find(s => s.key === active);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>{form.display_name || (form.first_name || form.last_name ? `${form.first_name || ""} ${form.last_name || ""}`.trim() : "Claimant")}</h2>
          <StatusPill sm status={form.claimant_type || "individual"}>{labelOf(CLAIMANT_TYPE, form.claimant_type || "individual")}</StatusPill>
          {!data.materialized && <span className="badge" style={{ background: "#fef3c7", color: "#92400e", fontSize: 10 }} title="No native claimant record saved yet — showing claim + VCA fallback. Saving creates the native record.">FALLBACK · VCA</span>}
        </div>
        {canEdit && !editing && <button className="btn btn-blue btn-sm" onClick={() => setEditing(true)}>✎ Edit Claimant</button>}
        {editing && <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={cancel} disabled={busy}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        </div>}
      </div>
      {err && <div className="alert-error" style={{ marginBottom: 12 }}>{err}</div>}

      {/* Section nav */}
      <div style={{ display: "flex", gap: 4, marginBottom: 14, flexWrap: "wrap" }}>
        {SECTIONS.map(s => (
          <button key={s.key} onClick={() => setActive(s.key)}
            style={{ padding: "5px 12px", fontSize: 12, fontWeight: 600, borderRadius: 14, cursor: "pointer",
              border: "1px solid " + (active === s.key ? "var(--navy)" : "var(--border)"),
              background: active === s.key ? "var(--navy)" : "transparent", color: active === s.key ? "#fff" : "var(--text2)" }}>{s.label}</button>
        ))}
      </div>

      {(() => {
        const isBlank = (f) => { const v = form[f.k]; return v == null || v === "" || (Array.isArray(v) && !v.length); };
        const fields = sec.fields.filter(f => !f.when || f.when(form));
        const populated = fields.filter(f => !isBlank(f));
        const emptyCount = fields.length - populated.length;
        // LD-DESIGN-012 pattern 2: Overview read view = Profile + Readiness +
        // Quick Facts cards. Editing keeps the full grid below.
        if (sec.key === "general" && !editing) {
          return <ClaimantOverviewCards form={form} emptyCount={emptyCount} canEdit={canEdit} />;
        }
        return <div className="card"><div className="card-body">
        {(() => {
          // A4: read mode shows only POPULATED fields (no wall of —); a fully-empty section
          // shows a guided empty state; editing shows the full grid so anything can be filled.
          if (!editing && populated.length === 0) {
            return <EmptyState icon="🧍" title={`No ${sec.label.toLowerCase()} information yet`} hint={canEdit ? "Click Edit Claimant to add it." : undefined} />;
          }
          const shown = editing ? fields : populated;
          return (<>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "12px 24px" }}>
              {shown.map(f => (
                <div key={f.k} style={f.full ? { gridColumn: "1 / -1" } : undefined}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3 }}>
                    {f.l}{f.canon && <span title="Edits the claim record (single source of truth)" style={{ marginLeft: 5, color: "var(--blue)", fontWeight: 400, textTransform: "none" }}>· claim</span>}
                  </div>
                  {editing ? <EditValue f={f} form={form} set={set} bodyParts={bodyParts} /> : <div style={{ fontSize: 13 }}><ReadValue f={f} form={form} /></div>}
                </div>
              ))}
            </div>
            {!editing && emptyCount > 0 && (
              <div style={{ marginTop: 12, fontSize: 11.5, color: "var(--text3)" }}>
                {emptyCount} more field{emptyCount > 1 ? "s" : ""} not yet filled{canEdit ? " · click Edit Claimant to add" : ""}.
              </div>
            )}
          </>);
        })()}
        </div></div>;
      })()}
    </div>
  );
}
